export class FormObject {
    constructor(initialData = {}) {
        this._data = {};
        this._schema = {};
        this._validators = {};
        
        // Initialize with provided data
        if (initialData && typeof initialData === 'object') {
            Object.keys(initialData).forEach(key => {
                this._data[key] = initialData[key];
            });
        }
    }

    // Define schema for the object
    defineSchema(schema) {
        this._schema = schema;
        
        // Initialize all schema fields with default values
        Object.keys(schema).forEach(key => {
            const field = schema[key];
            if (this._data[key] === undefined) {
                this._data[key] = this._getDefaultValue(field);
            }
        });
        
        return this;
    }

    // Get default value based on field type
    _getDefaultValue(field) {
        if (field.default !== undefined) {
            return typeof field.default === 'function' ? field.default() : field.default;
        }
        
        switch (field.type) {
            case 'string':
                return '';
            case 'number':
                return 0;
            case 'boolean':
                return false;
            case 'array':
                return [];
            case 'object':
                return {};
            case 'date':
                return null;
            default:
                return null;
        }
    }

    // Set a single attribute
    set(key, value) {
        // Validate if schema exists for this key
        if (this._schema[key] && this._schema[key].type) {
            const isValid = this._validateType(value, this._schema[key].type);
            if (!isValid) {
                console.warn(`Type mismatch for ${key}: expected ${this._schema[key].type}`);
            }
        }

        // Run custom validator if exists
        if (this._validators[key]) {
            const isValid = this._validators[key](value);
            if (!isValid) {
                console.warn(`Validation failed for ${key}`);
                return this;
            }
        }

        this._data[key] = value;
        return this;
    }

    // Get a single attribute
    get(key) {
        return this._data[key];
    }

    // Set multiple attributes at once
    setMultiple(data) {
        Object.keys(data).forEach(key => {
            this.set(key, data[key]);
        });
        return this;
    }

    // Add item to array attribute
    addToArray(key, value, unique = false) {
        if (!Array.isArray(this._data[key])) {
            this._data[key] = [];
        }

        if (unique) {
            // Check for uniqueness based on comparison
            const exists = this._data[key].some(item => 
                JSON.stringify(item) === JSON.stringify(value)
            );
            if (!exists) {
                this._data[key].push(value);
            }
        } else {
            this._data[key].push(value);
        }
        
        return this;
    }

    // Add item to array with custom comparison
    addToArrayBy(key, value, compareFn) {
        if (!Array.isArray(this._data[key])) {
            this._data[key] = [];
        }

        const exists = this._data[key].some(item => compareFn(item, value));
        if (!exists) {
            this._data[key].push(value);
        }
        
        return this;
    }

    // Remove item from array
    removeFromArray(key, compareFn) {
        if (!Array.isArray(this._data[key])) {
            return this;
        }

        this._data[key] = this._data[key].filter(item => !compareFn(item));
        return this;
    }

    // Update item in array
    updateInArray(key, findFn, updateFn) {
        if (!Array.isArray(this._data[key])) {
            return this;
        }

        const index = this._data[key].findIndex(findFn);
        if (index !== -1) {
            this._data[key][index] = updateFn(this._data[key][index]);
        }
        
        return this;
    }

    // Clear array
    clearArray(key) {
        if (Array.isArray(this._data[key])) {
            this._data[key] = [];
        }
        return this;
    }

    // Increment numeric value
    increment(key, amount = 1) {
        if (typeof this._data[key] === 'number') {
            this._data[key] += amount;
        } else {
            this._data[key] = amount;
        }
        return this;
    }

    // Decrement numeric value
    decrement(key, amount = 1) {
        if (typeof this._data[key] === 'number') {
            this._data[key] -= amount;
        }
        return this;
    }

    // Toggle boolean value
    toggle(key) {
        if (typeof this._data[key] === 'boolean') {
            this._data[key] = !this._data[key];
        } else {
            this._data[key] = true;
        }
        return this;
    }

    // Add or update in object/map
    setInObject(key, subKey, value) {
        if (typeof this._data[key] !== 'object' || Array.isArray(this._data[key])) {
            this._data[key] = {};
        }
        this._data[key][subKey] = value;
        return this;
    }

    // Get from nested object
    getFromObject(key, subKey) {
        if (typeof this._data[key] === 'object' && !Array.isArray(this._data[key])) {
            return this._data[key][subKey];
        }
        return undefined;
    }

    // Remove from object
    removeFromObject(key, subKey) {
        if (typeof this._data[key] === 'object' && !Array.isArray(this._data[key])) {
            delete this._data[key][subKey];
        }
        return this;
    }

    // Add custom validator
    addValidator(key, validatorFn) {
        this._validators[key] = validatorFn;
        return this;
    }

    // Validate type
    _validateType(value, type) {
        switch (type) {
            case 'string':
                return typeof value === 'string';
            case 'number':
                return typeof value === 'number';
            case 'boolean':
                return typeof value === 'boolean';
            case 'array':
                return Array.isArray(value);
            case 'object':
                return typeof value === 'object' && !Array.isArray(value);
            case 'date':
                return value instanceof Date || typeof value === 'string';
            default:
                return true;
        }
    }

    // Check if attribute exists
    has(key) {
        return this._data.hasOwnProperty(key);
    }

    // Remove attribute
    remove(key) {
        delete this._data[key];
        return this;
    }

    // Reset to initial/default state
    reset() {
        Object.keys(this._schema).forEach(key => {
            this._data[key] = this._getDefaultValue(this._schema[key]);
        });
        return this;
    }

    // Clear all data
    clear() {
        this._data = {};
        return this;
    }

    // Get all data
    getData() {
        return { ...this._data };
    }

    // Get raw data reference (use with caution)
    getRawData() {
        return this._data;
    }

    // Load from external data
    load(data) {
        if (data && typeof data === 'object') {
            Object.keys(data).forEach(key => {
                this.set(key, data[key]);
            });
        }
        return this;
    }

    // Export to specific format
    export(format = 'json', includedKeys = null) {
        const dataToExport = includedKeys 
            ? Object.keys(this._data)
                .filter(key => includedKeys.includes(key))
                .reduce((obj, key) => {
                    obj[key] = this._data[key];
                    return obj;
                }, {})
            : this._data;

        switch (format) {
            case 'json':
                return JSON.parse(JSON.stringify(dataToExport));
            case 'string':
                return JSON.stringify(dataToExport);
            default:
                return dataToExport;
        }
    }

    // Clone the object
    clone() {
        const cloned = new FormObject();
        cloned._data = JSON.parse(JSON.stringify(this._data));
        cloned._schema = { ...this._schema };
        cloned._validators = { ...this._validators };
        return cloned;
    }

    // Check if empty
    isEmpty() {
        return Object.keys(this._data).length === 0;
    }

    // Get keys
    keys() {
        return Object.keys(this._data);
    }

    // Get values
    values() {
        return Object.values(this._data);
    }

    // Get entries
    entries() {
        return Object.entries(this._data);
    }

    // Map over data
    map(fn) {
        return Object.keys(this._data).map(key => fn(key, this._data[key]));
    }

    // Filter data
    filter(fn) {
        const filtered = {};
        Object.keys(this._data).forEach(key => {
            if (fn(key, this._data[key])) {
                filtered[key] = this._data[key];
            }
        });
        return filtered;
    }

    // Transform data
    transform(transformFn) {
        return transformFn(this._data);
    }

    // Validate all data
    validate() {
        const errors = {};
        
        // Schema validation
        Object.keys(this._schema).forEach(key => {
            const field = this._schema[key];
            const value = this._data[key];
            
            // Required check
            if (field.required && (value === null || value === undefined || value === '')) {
                errors[key] = `${key} is required`;
            }
            
            // Type check
            if (value !== null && value !== undefined) {
                const isValid = this._validateType(value, field.type);
                if (!isValid) {
                    errors[key] = `${key} must be of type ${field.type}`;
                }
            }
        });

        // Custom validators
        Object.keys(this._validators).forEach(key => {
            const isValid = this._validators[key](this._data[key]);
            if (!isValid) {
                errors[key] = `${key} failed custom validation`;
            }
        });

        return {
            isValid: Object.keys(errors).length === 0,
            errors
        };
    }

    // Get summary
    getSummary() {
        return {
            keys: Object.keys(this._data),
            count: Object.keys(this._data).length,
            hasSchema: Object.keys(this._schema).length > 0,
            hasValidators: Object.keys(this._validators).length > 0
        };
    }

    // Static method to create from schema
    static fromSchema(schema) {
        const form = new FormObject();
        form.defineSchema(schema);
        return form;
    }

    // Debug output
    debug() {
        console.log('FormObject Data:', this._data);
        console.log('FormObject Schema:', this._schema);
        console.log('FormObject Validators:', Object.keys(this._validators));
        return this;
    }
}

// Helper function to create FormObject
export const createFormObject = (initialData = {}) => new FormObject(initialData);

// Helper function to create from schema
export const createFromSchema = (schema) => FormObject.fromSchema(schema);